import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Truck, Building2, Warehouse, Users } from 'lucide-react';
import { searchAPI } from '../api';

export const TYPE_META = {
  load: { label: 'Load', Icon: Truck, color: '#F8191B' },
  customer: { label: 'Customer', Icon: Users, color: '#7c3aed' },
  carrier: { label: 'Carrier', Icon: Building2, color: '#0ea5e9' },
  facility: { label: 'Facility', Icon: Warehouse, color: '#10b759' },
};

export const Highlight = ({ text, query }) => {
  const t = String(text ?? '');
  if (!query) return <>{t}</>;
  const i = t.toLowerCase().indexOf(query.toLowerCase());
  if (i === -1) return <>{t}</>;
  return (<>{t.slice(0, i)}<mark style={{ background: '#fff3bf', color: 'inherit', padding: 0, borderRadius: 2 }}>{t.slice(i, i + query.length)}</mark>{t.slice(i + query.length)}</>);
};

export const flattenResults = (data) => [
  ...(data?.loads || []), ...(data?.customers || []), ...(data?.carriers || []), ...(data?.facilities || []),
];

// One suggestion row: badge (type + matched field) → primary → secondary
export const SearchResultRow = ({ r, query, onClick, active, testId }) => {
  const meta = TYPE_META[r.type];
  const primary = r.type === 'load'
    ? (r.matched_field === 'Load #' ? `Load #${r.load_number || ''}` : `${r.matched_field} ${r.matched_value}`)
    : `${r.matched_field === meta.label ? '' : `${r.matched_field} `}${r.matched_value}`;
  const secondary = r.type === 'load'
    ? [r.matched_field !== 'Load #' ? `Load #${r.load_number || ''}` : null, r.customer_name, r.route].filter(Boolean).join(' • ')
    : [r.matched_field !== meta.label ? r.name : null, r.subtitle].filter(Boolean).join(' • ');
  return (
    <div
      onMouseDown={(e) => { e.preventDefault(); onClick(r); }}
      className="flex items-start gap-3 px-3 py-2 cursor-pointer"
      style={{ background: active ? 'var(--tms-hover, #f3f4f8)' : 'transparent' }}
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 mt-0.5" style={{ background: `${meta.color}1a`, color: meta.color }}>
        <meta.Icon className="w-3 h-3" /> {meta.label}{r.type === 'load' && r.matched_field !== 'Load #' ? ` · ${r.matched_field}` : ''}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[var(--tms-text)] truncate"><Highlight text={primary} query={query} /></div>
        {secondary && <div className="text-xs text-[var(--tms-text-muted)] truncate"><Highlight text={secondary} query={query} /></div>}
      </div>
      {r.status && <span className="text-[10px] text-[var(--tms-text-muted)] shrink-0 mt-1">{r.status}</span>}
    </div>
  );
};

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const run = useCallback(async (q) => {
    setLoading(true);
    try { const res = await searchAPI.search(q, 5); setData(res.data); setOpen(true); setActive(-1); }
    catch (err) { console.debug('search failed', err?.message); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounceRef.current);
    if (!v.trim()) { setData(null); setOpen(false); return; }
    debounceRef.current = setTimeout(() => run(v.trim()), 220);
  };

  const go = (r) => { setQuery(''); setData(null); setOpen(false); navigate(r.url); };
  const flat = flattenResults(data);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, -1)); }
    else if (e.key === 'Enter' && query.trim()) {
      e.preventDefault();
      if (active >= 0 && flat[active]) go(flat[active]);
      else if (flat.length === 1) go(flat[0]);
      else { setOpen(false); navigate(`/search?q=${encodeURIComponent(query.trim())}`); }
    } else if (e.key === 'Escape') setOpen(false);
  };

  const groups = [['loads', 'Loads'], ['customers', 'Customers'], ['carriers', 'Carriers'], ['facilities', 'Facilities']];
  let idx = -1;

  return (
    <div className="nui-search-form" ref={boxRef} role="search" style={{ position: 'relative' }}>
      <Search className="search-icon" aria-hidden="true" />
      <input
        type="text"
        className="nui-search-input"
        placeholder="Search loads, PO#, BOL#, carriers, facilities..."
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (flat.length) setOpen(true); }}
        aria-label="Global search"
        data-testid="global-search-input"
      />
      {open && (
        <div className="nui-search-results" style={{ padding: 0, maxHeight: 480, overflowY: 'auto', width: 'min(640px, 90vw)' }} data-testid="global-search-results">
          {flat.length === 0 ? (
            <div className="nui-search-empty">{loading ? 'Searching…' : `No matches for "${query}"`}</div>
          ) : (
            <>
              {groups.map(([key, label]) => (data?.[key] || []).length > 0 && (
                <div key={key}>
                  <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[var(--tms-text-muted)] border-t first:border-t-0 border-[var(--tms-border)]">{label}</div>
                  {data[key].map((r) => { idx += 1; const i = idx; return (
                    <SearchResultRow key={`${r.type}-${r.id}`} r={r} query={query} onClick={go} active={i === active} testId={`global-search-result-${r.type}-${r.id}`} />
                  ); })}
                </div>
              ))}
              <div
                className="px-3 py-2 text-xs text-[var(--tms-primary)] font-semibold cursor-pointer border-t border-[var(--tms-border)]"
                onMouseDown={(e) => { e.preventDefault(); setOpen(false); navigate(`/search?q=${encodeURIComponent(query.trim())}`); }}
                data-testid="global-search-see-all"
              >
                See all results for "{query}" ↵
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
